import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { SeedDraftItem } from '@/types/seeds';

export interface AIDraftReviewTableProps {
  drafts: SeedDraftItem[];
  selectedIndices: Set<number>;
  onToggle: (index: number) => void;
  onUpdate: (index: number, patch: Partial<SeedDraftItem>) => void;
}

/**
 * Inline-editable list of AI-generated draft seeds. Each row has a checkbox;
 * only checked drafts are committed. Each field is editable directly.
 */
export function AIDraftReviewTable({
  drafts,
  selectedIndices,
  onToggle,
  onUpdate,
}: AIDraftReviewTableProps) {
  return (
    <div className="max-h-[480px] space-y-2 overflow-y-auto rounded-md border p-2">
      {drafts.map((d, i) => {
        const checked = selectedIndices.has(i);
        return (
          <div
            key={i}
            className={`space-y-2 rounded-md border p-2 ${
              checked ? 'border-primary' : 'border-muted'
            }`}
          >
            <div className="flex items-start gap-2">
              <Checkbox checked={checked} onCheckedChange={() => onToggle(i)} />
              <div className="flex-1 space-y-2">
                <Input
                  value={d.title}
                  onChange={(e) => onUpdate(i, { title: e.target.value })}
                  placeholder="Title"
                  className="font-medium"
                />
                <textarea
                  value={d.description}
                  onChange={(e) => onUpdate(i, { description: e.target.value })}
                  placeholder="Description"
                  className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  rows={3}
                />
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-muted-foreground">Min:</label>
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={d.estimated_minutes}
                      onChange={(e) =>
                        onUpdate(i, { estimated_minutes: Number(e.target.value) || 1 })
                      }
                      className="h-7 w-16"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-muted-foreground">Difficulty:</label>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={d.difficulty_level}
                      onChange={(e) =>
                        onUpdate(i, { difficulty_level: Number(e.target.value) || 1 })
                      }
                      className="h-7 w-16"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
